/**
 * Turns the raw strings the auth flow throws — Auth0 error codes, fetch
 * failures, our own guard messages — into one sentence a visitor can act on.
 *
 * Login.vue used to print `session.errorMessage` verbatim, so a visitor saw
 * "invalid_grant" or "Failed to fetch" under the sign-in button. The raw
 * text is still kept (the caller puts it in a `title`) for anyone debugging.
 */
const GENERIC = "Sign-in did not complete. Please try again.";
const MAX_PASSTHROUGH_CHARS = 140;

const RULES: { test: RegExp; message: string }[] = [
  {
    test: /access_denied|unauthori[sz]ed|not authori[sz]ed|user is blocked|blocked/i,
    message: "This account is not allowed to sign in here. Try a different account, or contact support.",
  },
  {
    test: /invalid_grant|login_required|interaction_required|consent_required|expired/i,
    message: "Your session has expired. Please sign in again.",
  },
  {
    test: /state mismatch|no transaction state/i,
    message: "The sign-in link has expired or was opened in another tab. Please sign in again.",
  },
  {
    test: /failed to fetch|networkerror|network request failed|load failed|timed out|timeout/i,
    message: "We could not reach the sign-in service. Check your connection and try again.",
  },
  { test: /token exchange failed|failed to fetch user info|authentication failed/i, message: GENERIC },
  { test: /could not restore your session/i, message: "Your session could not be restored. Please sign in again." },
];

export function humaniseAuthError(raw: string | null | undefined): string {
  const text = (raw ?? "").trim();
  if (!text) return GENERIC;
  const rule = RULES.find((r) => r.test.test(text));
  if (rule) return rule.message;
  // Auth0's own `error_description` is often already a sentence ("Wrong
  // email or password.") — keep it, but never let a stack-trace-length
  // string onto the page.
  return text.length > MAX_PASSTHROUGH_CHARS ? `${text.slice(0, MAX_PASSTHROUGH_CHARS - 1)}…` : text;
}
